import { useState, useEffect, useRef } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Gift, Sparkles, X, Star, Trophy } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface Prize {
  id: string;
  name: string;
  win_percentage: number;
  prize_type: 'discount' | 'free_item' | 'no_prize';
  prize_value: string;
  color: string;
  order_position: number;
}

interface WheelConfig {
  id: string;
  title: string;
  primary_color: string;
  secondary_color: string;
}

interface WheelOfFortuneProps {
  restaurantId: string;
  onClose: () => void;
}

export const WheelOfFortune = ({ restaurantId, onClose }: WheelOfFortuneProps) => {
  const { toast } = useToast();
  const [config, setConfig] = useState<WheelConfig | null>(null);
  const [prizes, setPrizes] = useState<Prize[]>([]);
  const [email, setEmail] = useState('');
  const [isSpinning, setIsSpinning] = useState(false);
  const [showEmailForm, setShowEmailForm] = useState(true);
  const [wonPrize, setWonPrize] = useState<Prize | null>(null);
  const [hasSpunToday, setHasSpunToday] = useState(false);
  const [rotation, setRotation] = useState(0);

  useEffect(() => {
    loadWheelData();
    checkExistingSpin();
  }, [restaurantId]);


  const loadWheelData = async () => {
    try {
      const { data: configData } = await supabase
        .from('wheel_configurations')
        .select('*')
        .eq('restaurant_id', restaurantId)
        .eq('is_enabled', true)
        .single();

      if (!configData) {
        onClose();
        return;
      }

      setConfig(configData);

      const { data: prizesData } = await supabase
        .from('wheel_prizes')
        .select('*')
        .eq('wheel_configuration_id', configData.id)
        .order('order_position');

      setPrizes((prizesData || []).map(p => ({
        ...p,
        prize_type: p.prize_type as 'discount' | 'free_item' | 'no_prize'
      })));
    } catch (error) {
      onClose();
    }
  };

  const checkExistingSpin = () => {
    const today = new Date().toDateString();
    const lastSpin = localStorage.getItem(`wheel_spin_${restaurantId}`);
    if (lastSpin === today) {
      setHasSpunToday(true);
      setShowEmailForm(false);
    }
  };

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const selectWinner = (prizes: Prize[]): Prize => {
    const random = Math.random() * 100;
    let cumulative = 0;

    for (const prize of prizes) {
      cumulative += prize.win_percentage;
      if (random <= cumulative) {
        return prize;
      }
    }

    return prizes[prizes.length - 1];
  };

  const spinWheel = async () => {
    if (!email || !validateEmail(email)) {
      toast({ title: 'Email invalide', description: 'Veuillez entrer une adresse email valide', variant: 'destructive' });
      return;
    }

    if (hasSpunToday) {
      toast({ title: 'Déjà joué', description: 'Vous avez déjà tenté votre chance aujourd\'hui !', variant: 'destructive' });
      return;
    }

    setIsSpinning(true);
    
    try {
      // Appeler l'edge function spin-wheel
      const { data: spinResult, error: spinError } = await supabase.functions.invoke('spin-wheel', {
        body: {
          restaurantId: restaurantId,
          customerEmail: email,
          userAgent: navigator.userAgent,
          ipAddress: await getClientIP()
        }
      });

      if (spinError) {
        toast({ 
          title: 'Erreur', 
          description: `Erreur: ${spinError.message}`,
          variant: 'destructive'
        });
        setIsSpinning(false);
        return;
      }

      if (!spinResult?.success) {
        toast({ 
          title: 'Échec', 
          description: spinResult?.message || 'Le spin a échoué',
          variant: 'destructive'
        });
        setIsSpinning(false);
        return;
      }

      // Animer la roue
      const selectedPrize = spinResult.prize;
      const winningIndex = prizes.findIndex(p => p.id === selectedPrize.id);
      const baseRotation = rotation;
      const spins = 7 + Math.floor(Math.random() * 3); // 7-9 tours complets pour plus de spectacle
      const finalAngle = winningIndex * segmentAngle;
      const newRotation = baseRotation + (spins * 360) + (360 - finalAngle);
      
      setRotation(newRotation);
      
      // Attendre la fin de l'animation
      setTimeout(() => {
        setWonPrize(selectedPrize);
        
        // Marquer comme joué aujourd'hui
        const today = new Date().toDateString();
        localStorage.setItem(`wheel_spin_${restaurantId}`, today);
        setHasSpunToday(true);
        setShowEmailForm(false);

        // Afficher le résultat
        if (selectedPrize.type !== 'no_prize') {
          toast({ 
            title: '🎉 Félicitations !', 
            description: `Vous avez gagné : ${selectedPrize.name}. Un email vous a été envoyé !`,
            duration: 8000
          });
        } else {
          toast({ 
            title: 'Dommage...', 
            description: 'Ce n\'est pas gagnant cette fois ! Revenez demain !',
            variant: 'destructive'
          });
        }
        
        setIsSpinning(false);
      }, 4000);

    } catch (error: any) {
      toast({ 
        title: 'Erreur', 
        description: `Exception: ${error.message}`,
        variant: 'destructive'
      });
      setIsSpinning(false);
    }
  };

  const getClientIP = async (): Promise<string> => {
    try {
      const response = await fetch('https://api.ipify.org?format=json');
      const data = await response.json();
      return data.ip;
    } catch {
      return 'unknown';
    }
  };

  if (!config || !prizes.length) {
    return null;
  }

  const segmentAngle = 360 / prizes.length;

  return (
    <Sheet open={true} onOpenChange={(open) => !open && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto p-4">
        <SheetHeader className="pb-4">
          <SheetTitle className="flex items-center gap-2">
            <Gift className="h-5 w-5" style={{ color: config.primary_color }} />
            {config.title}
          </SheetTitle>
        </SheetHeader>

        <div className="space-y-6">
          {showEmailForm && !hasSpunToday && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground text-center">
                Entrez votre email pour tenter votre chance !
              </p>
              <div className="space-y-2">
                <Label htmlFor="email">Votre adresse email</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="votre@email.com"
                  disabled={isSpinning}
                />
              </div>
            </div>
          )}

          {/* Espace entre l'email et la roue */}
          <div className="h-6"></div>

          {/* Roue de la fortune moderne et élégante */}
          <div className="flex justify-center relative">
            <div className="relative">
              {/* Conteneur de la roue avec effet de profondeur */}
              <div className="relative w-64 h-64 sm:w-72 sm:h-72 rounded-full shadow-2xl bg-white p-2">
                <div 
                  className="relative w-full h-full rounded-full overflow-hidden"
                  style={{
                    background: `conic-gradient(from 0deg, ${prizes.map((prize, index) => 
                      `${prize.color} ${(index * segmentAngle)}deg ${((index + 1) * segmentAngle)}deg`
                    ).join(', ')})`
                  }}
                >
                  {/* SVG overlay pour les textes et détails */}
                  <svg
                    width="100%"
                    height="100%"
                    className={`absolute top-0 left-0 ${isSpinning ? 'animate-spin-wheel' : 'transition-transform duration-1000 ease-out'}`}
                    style={{ 
                      transform: `rotate(${rotation}deg)`,
                      transformOrigin: 'center center'
                    }}
                    viewBox="0 0 320 320"
                  >
                    {prizes.map((prize, index) => {
                      const angle = (index * segmentAngle) + (segmentAngle / 2);
                      const textRadius = 110;
                      const textX = 160 + textRadius * Math.cos((angle - 90) * Math.PI / 180);
                      const textY = 160 + textRadius * Math.sin((angle - 90) * Math.PI / 180);
                      
                      // Icône position
                      const iconRadius = 80;
                      const iconX = 160 + iconRadius * Math.cos((angle - 90) * Math.PI / 180);
                      const iconY = 160 + iconRadius * Math.sin((angle - 90) * Math.PI / 180);

                      return (
                        <g key={prize.id}>
                          {/* Ligne de séparation */}
                          <line
                            x1="160"
                            y1="160"
                            x2={160 + 150 * Math.cos((index * segmentAngle - 90) * Math.PI / 180)}
                            y2={160 + 150 * Math.sin((index * segmentAngle - 90) * Math.PI / 180)}
                            stroke="white"
                            strokeWidth="2"
                            opacity="0.7"
                          />
                          
                          {/* Icône du prix */}
                          {prize.prize_type !== 'no_prize' ? (
                            <circle
                              cx={iconX}
                              cy={iconY}
                              r="12"
                              fill="#FFD700"
                              stroke="white"
                              strokeWidth="2"
                            />
                          ) : (
                            <circle
                              cx={iconX}
                              cy={iconY}
                              r="12"
                              fill="#64748b"
                              stroke="white"
                              strokeWidth="2"
                            />
                          )}
                          
                          {/* Texte du prix */}
                          <text
                            x={textX}
                            y={textY}
                            fill="white"
                            fontSize="12"
                            fontWeight="bold"
                            textAnchor="middle"
                            dominantBaseline="middle"
                            transform={`rotate(${angle}, ${textX}, ${textY})`}
                            className="drop-shadow-lg font-sans"
                          >
                            {prize.name.length > 15 ? prize.name.substring(0, 15) + '...' : prize.name}
                          </text>
                          
                          {/* Valeur du prix (si applicable) */}
                          {prize.prize_value && prize.prize_type !== 'no_prize' && (
                            <text
                              x={textX}
                              y={textY + 15}
                              fill="white"
                              fontSize="10"
                              fontWeight="600"
                              textAnchor="middle"
                              dominantBaseline="middle"
                              transform={`rotate(${angle}, ${textX}, ${textY + 15})`}
                              className="drop-shadow-lg opacity-90"
                            >
                              {prize.prize_value}
                            </text>
                          )}
                        </g>
                      );
                    })}
                  </svg>

                  {/* Centre de la roue avec logo */}
                  <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-16 h-16 bg-white rounded-full shadow-xl flex items-center justify-center border-4 border-gray-200">
                    <Trophy className="w-8 h-8 text-yellow-500" />
                  </div>
                </div>
              </div>

              {/* Pointeur élégant corrigé */}
              <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-4 z-20">
                <div className="relative">
                  <div 
                    className="w-0 h-0 border-l-[16px] border-r-[16px] border-t-[32px] border-l-transparent border-r-transparent drop-shadow-xl"
                    style={{ borderTopColor: config.primary_color }}
                  />
                  <div className="absolute -top-8 left-1/2 transform -translate-x-1/2">
                    <div 
                      className="w-8 h-8 rounded-full border-4 border-white shadow-xl flex items-center justify-center"
                      style={{ backgroundColor: config.primary_color }}
                    >
                      <Star className="w-4 h-4 text-white" />
                    </div>
                  </div>
                </div>
              </div>

              {/* Effet de particules lors du spin */}
              {isSpinning && (
                <div className="absolute inset-0 pointer-events-none">
                  {[...Array(12)].map((_, i) => (
                    <div
                      key={i}
                      className={`absolute w-2 h-2 bg-yellow-400 rounded-full animate-ping`}
                      style={{
                        top: '50%',
                        left: '50%',
                        transform: `translate(-50%, -50%) rotate(${i * 30}deg) translateY(-140px)`,
                        animationDelay: `${i * 0.1}s`,
                        animationDuration: '1s'
                      }}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>


          {hasSpunToday ? (
            <div className="text-center space-y-2">
              <p className="text-sm text-muted-foreground">
                Vous avez déjà tenté votre chance aujourd'hui !
              </p>
              <p className="text-xs text-muted-foreground">
                Revenez demain pour une nouvelle chance !
              </p>
            </div>
          ) : (
            <Button
              onClick={spinWheel}
              disabled={isSpinning || !email || !validateEmail(email)}
              className="w-full gap-2"
              style={{ 
                backgroundColor: config.primary_color,
                borderColor: config.primary_color 
              }}
            >
              {isSpinning ? (
                <>
                  <Sparkles className="h-4 w-4 animate-spin" />
                  Rotation en cours...
                </>
              ) : (
                <>
                  <Gift className="h-4 w-4" />
                  Faire tourner la roue !
                </>
              )}
            </Button>
          )}

          {wonPrize && wonPrize.prize_type !== 'no_prize' && (
            <div className="p-4 bg-green-50 border border-green-200 rounded-lg text-center">
              <h3 className="font-semibold text-green-800">🎉 Vous avez gagné !</h3>
              <p className="text-green-700">{wonPrize.name}</p>
              <p className="text-xs text-green-600 mt-2">
                Un email vous sera envoyé avec les détails pour récupérer votre gain.
              </p>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};